import type { Epic } from '../../index.js'
import type { Action } from '@reduxjs/toolkit'
import { filter, from, mergeMap, of, startWith, catchError, withLatestFrom } from 'rxjs'
import { chatSlice } from '../../Slice/Chat/ChatSlice.js'

/**
 * Renders the agent-authored report document (IR-1) after each turn that changed it. Reacts to
 * `applyReportEdits` — which the SendMessageEpic dispatches once all of a turn's report directives have been
 * folded into `currentReport` — and issues exactly ONE server render off the RESULTING blocks (epics run after
 * the reducer, so `withLatestFrom(state$)` already sees the post-edit state).
 *
 * Mirrors the graph-canvas pattern: the slice holds the state, the epic does the async render. Failure is
 * contained (`status: 'error'`) so a bad render never crashes the chat — the prior `html` stays on screen.
 * Scope comes from `chat.scopeKey` (`${scopeType}:${scopeId}`); a malformed/absent key is a no-op.
 */
export const RenderReportEpic: Epic = (action$, state$, { ChatRepository, [chatSlice.name]: chat }) =>
  action$.pipe(
    filter(chat.applyReportEdits.match),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const report = state.chat?.currentReport
      const scopeKey = state.chat?.scopeKey ?? ''
      const sep = scopeKey.indexOf(':')
      const scopeType = sep >= 0 ? scopeKey.slice(0, sep) : ''
      const scopeId = sep >= 0 ? scopeKey.slice(sep + 1) : ''

      if (!report || !scopeType || !scopeId) return of<Action>()

      return from(ChatRepository.renderReportDoc(scopeType, scopeId, report.blocks, report.title)).pipe(
        mergeMap((response) => of<Action>(chat.setReportRender({ html: response.html, status: 'idle' }))),
        catchError(() => of<Action>(chat.setReportRender({ status: 'error' }))),
        startWith(chat.setReportRender({ status: 'rendering' }))
      )
    })
  )
